import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

const AuthCallback = () => {
  const navigate = useNavigate();
  const [error, setError] = useState('');

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Get role from query params (passed via redirectTo)
        const params = new URLSearchParams(window.location.search);
        const role = params?.get('role') || 'influencer';
        const code = params?.get('code');
        const errorParam = params?.get('error');
        const errorDescription = params?.get('error_description');

        // Handle OAuth error returned from provider
        if (errorParam) {
          console.error('OAuth error:', errorParam, errorDescription);
          setError(errorDescription || errorParam);
          setTimeout(() => navigate('/login-screen'), 3000);
          return;
        }

        let session = null;

        if (code) {
          // PKCE flow: exchange the authorization code for a session
          const { data, error: exchangeError } = await supabase?.auth?.exchangeCodeForSession(code);
          if (exchangeError) {
            console.error('Code exchange error:', exchangeError);
            setError(exchangeError?.message);
            setTimeout(() => navigate('/login-screen'), 3000);
            return;
          }
          session = data?.session;
        } else {
          // Implicit flow fallback: session may already be set via hash fragment
          const { data, error: sessionError } = await supabase?.auth?.getSession();
          if (sessionError) {
            console.error('Auth callback error:', sessionError);
            setError(sessionError?.message);
            setTimeout(() => navigate('/login-screen'), 3000);
            return;
          }
          session = data?.session;
        }

        if (session?.user) {
          // Update user metadata with selected role if not already set
          const existingRole = session?.user?.user_metadata?.role;
          if (!existingRole) {
            await supabase?.auth?.updateUser({ data: { role } });
          }

          const finalRole = existingRole || role;

          // Admin users skip verification
          if (finalRole === 'admin') {
            navigate('/admin-panel');
            return;
          }

          // Check profile verification status
          const { data: profile } = await supabase
            ?.from('user_profiles')
            ?.select('profile_verified')
            ?.eq('id', session?.user?.id)
            ?.single();

          if (profile?.profile_verified) {
            if (finalRole === 'brand') {
              navigate('/brand-dashboard');
            } else {
              navigate('/user-dashboard');
            }
          } else {
            navigate('/profile-verification-screen');
          }
        } else {
          navigate('/login-screen');
        }
      } catch (err) {
        console.error('Callback handling error:', err);
        navigate('/login-screen');
      }
    };

    handleCallback();
  }, [navigate]);

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center p-8 rounded-xl bg-card border border-border max-w-md">
          <div className="text-error text-lg font-medium mb-2">Authentication Failed</div>
          <p className="text-muted-foreground text-sm mb-4">{error}</p>
          <p className="text-muted-foreground text-xs">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-foreground font-medium">Completing sign-in...</p>
        <p className="text-muted-foreground text-sm mt-1">Please wait while we verify your account</p>
      </div>
    </div>
  );
};

export default AuthCallback;
